import { useId } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Expanded, Row, Typography } from '@tempmee/react';
import { OutlineSearchIcon } from '@tempmee/react-icons';
import { showErrorMessage, showSuccessMessage } from '../../../../../utils';

/**
 * Opens the licensing board (or NPI registry) and copies the entered number so CS can paste
 * it into the board's search. The copy is best-effort: the board opens either way.
 */
export const LookupButton = ({ url, copyValue }) => {
  const handleLookup = async () => {
    // Open first, inside the click, so popup blockers treat it as user-initiated.
    window.open(url, '_blank', 'noopener,noreferrer');
    if (!copyValue || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(copyValue);
      showSuccessMessage({ message: `Copied ${copyValue}` });
    } catch {
      // Clipboard permission denied: the board is already open, so say what to do instead.
      showErrorMessage({ message: `Couldn't copy ${copyValue}. Copy it from the card.` });
    }
  };

  return (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      startIcon={<OutlineSearchIcon size={16} />}
      onClick={handleLookup}
    >
      Lookup
    </Button>
  );
};

LookupButton.propTypes = {
  url: PropTypes.string.isRequired,
  /** The license or NPI number to copy; nothing is copied when empty. */
  copyValue: PropTypes.string,
};

LookupButton.defaultProps = {
  copyValue: '',
};

/**
 * One credential of an authorization: its status row, title, optional board lookup, and the
 * credential's own fields as children.
 */
export const CredentialCard = ({ statusRow, title, requirement, subtitle, lookup, children }) => {
  const headingId = useId();
  return (
    // A named group, so assistive tech announces which credential each control belongs to.
    <Card outline className="h-full" role="group" aria-labelledby={headingId}>
      {statusRow}
      <Row justify="between" align="start" gap={2} px={2} pt={6} pb={2}>
        {/* Same heading style as "Name Change Documents" in ConfirmedNameSection. */}
        <Expanded direction="column" gap={1} className="min-w-0">
          <Typography variant="subtitle1" as="h4" id={headingId}>
            {title}{' '}
            <Typography as="span" variant="body2" className="text-neutral-500">
              {requirement}
            </Typography>
          </Typography>
          {subtitle && (
            <Typography variant="body2" className="text-neutral-500">
              {subtitle}
            </Typography>
          )}
        </Expanded>
        {lookup && <LookupButton url={lookup.url} copyValue={lookup.copyValue} />}
      </Row>
      {children}
    </Card>
  );
};

CredentialCard.propTypes = {
  /** The status label or select, rendered as the card's first row. */
  statusRow: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  /** "Required(*)" or "(Optional)". */
  requirement: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  /** Board lookup; omitted when the state has no board URL. */
  lookup: PropTypes.shape({ url: PropTypes.string.isRequired, copyValue: PropTypes.string }),
  children: PropTypes.node,
};

CredentialCard.defaultProps = {
  subtitle: null,
  lookup: null,
  children: null,
};
